// /menus/periods — Menu Periods (Dayparts)
//
// Define Breakfast / Lunch / Dinner (or custom) time windows and assign which
// menu items are orderable during each. Backed by the existing menu_periods
// API (services/api/src/menu/menu-periods.controller.ts). MANAGER+ to edit.

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Button,
  Input,
  Select,
  Badge,
  Modal,
  Skeleton,
  EmptyState,
  useToast,
} from '@bbpos/ui';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { locationService } from '@/services/location.service';
import { productService } from '@/services/product.service';
import { menuPeriodService } from '@/services/menu-period.service';
import type { MenuPeriod } from '@/services/menu-period.service';
import { GLASS, cn } from '@/constants/design-tokens';
import { ApiError } from '@/lib/api-client';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const ALL_LOCATIONS = ''; // filter sentinel — list dayparts across every outlet

interface Preset {
  label: string;
  name: string;
  start: string;
  end: string;
}
const PRESETS: Preset[] = [
  { label: 'Breakfast', name: 'Breakfast', start: '07:00', end: '11:00' },
  { label: 'Lunch', name: 'Lunch', start: '11:00', end: '16:00' },
  { label: 'Dinner', name: 'Dinner', start: '16:00', end: '23:00' },
];

const hhmm = (t: string | undefined) => (t ?? '').slice(0, 5);

function PlusIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function MenuPeriodsContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([]);
  // Listing filter: '' = All Locations (so a daypart created under any outlet is visible).
  const [filterLocationId, setFilterLocationId] = useState<string>(selectedLocationId ?? ALL_LOCATIONS);
  const [products, setProducts] = useState<{ value: string; label: string }[]>([]);
  const [periods, setPeriods] = useState<MenuPeriod[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ── Modal state ──────────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MenuPeriod | null>(null);
  const [modalLocationId, setModalLocationId] = useState<string>('');
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('11:00');
  const [days, setDays] = useState<number[]>(ALL_DAYS);
  const [productIds, setProductIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const locationNameById = useMemo(() => new Map(locationOptions.map((o) => [o.value, o.label])), [locationOptions]);
  const filterOptions = useMemo(
    () => [{ value: ALL_LOCATIONS, label: 'All Locations' }, ...locationOptions],
    [locationOptions],
  );

  // Load outlets once.
  useEffect(() => {
    locationService
      .list({ is_active: 'true', limit: 100 })
      .then((res) => setLocationOptions((res.data ?? []).map((l) => ({ value: l.id, label: l.name }))))
      .catch(() => { /* silent */ });
  }, []);

  // Products for the item picker, scoped to the daypart's location (modal).
  useEffect(() => {
    productService
      .list({ location_id: modalLocationId || undefined, limit: 200 })
      .then((res) => setProducts((res.data ?? []).map((p: { id: string; name: string }) => ({ value: p.id, label: p.name }))))
      .catch(() => setProducts([]));
  }, [modalLocationId]);

  const fetchPeriods = useCallback(() => {
    setIsLoading(true);
    menuPeriodService
      .list(filterLocationId || undefined)
      .then((res) => setPeriods(res.data ?? []))
      .catch((err) => toast({ title: err instanceof ApiError ? err.message : 'Failed to load dayparts', variant: 'error' }))
      .finally(() => setIsLoading(false));
  }, [filterLocationId, toast]);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  const productNameById = useMemo(() => new Map(products.map((p) => [p.value, p.label])), [products]);

  function openCreate() {
    setEditing(null);
    // Default the new daypart's outlet to the filtered one, else the first outlet.
    setModalLocationId(filterLocationId || locationOptions[0]?.value || '');
    setName('');
    setStartTime('07:00');
    setEndTime('11:00');
    setDays(ALL_DAYS);
    setProductIds([]);
    setFormError(null);
    setModalOpen(true);
  }

  function openEdit(p: MenuPeriod) {
    setEditing(p);
    setModalLocationId(p.location_id);
    setName(p.name);
    setStartTime(hhmm(p.start_time));
    setEndTime(hhmm(p.end_time));
    setDays(p.days_of_week ?? ALL_DAYS);
    setProductIds(p.product_ids ?? []);
    setFormError(null);
    setModalOpen(true);
  }

  function applyPreset(p: Preset) {
    setName(p.name);
    setStartTime(p.start);
    setEndTime(p.end);
  }

  function toggleDay(d: number) {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)));
  }

  const handleSave = useCallback(async () => {
    if (!modalLocationId) { setFormError('Select a location for this daypart'); return; }
    if (!name.trim()) { setFormError('Name is required'); return; }
    if (!startTime || !endTime) { setFormError('Start and end time are required'); return; }
    if (days.length === 0) { setFormError('Select at least one day'); return; }
    if (productIds.length === 0) { setFormError('Assign at least one item to this daypart'); return; }
    setIsSaving(true);
    try {
      if (editing) {
        await menuPeriodService.update(editing.id, { name: name.trim(), start_time: startTime, end_time: endTime, days_of_week: days, product_ids: productIds });
        toast({ title: `"${name.trim()}" updated`, variant: 'success' });
      } else {
        await menuPeriodService.create({ location_id: modalLocationId, name: name.trim(), start_time: startTime, end_time: endTime, days_of_week: days, product_ids: productIds });
        toast({ title: `"${name.trim()}" daypart created`, variant: 'success' });
      }
      setModalOpen(false);
      fetchPeriods();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to save daypart');
    } finally {
      setIsSaving(false);
    }
  }, [modalLocationId, name, startTime, endTime, days, productIds, editing, fetchPeriods, toast]);

  const handleDeactivate = useCallback(async (p: MenuPeriod) => {
    try {
      await menuPeriodService.deactivate(p.id);
      toast({ title: `"${p.name}" removed`, variant: 'success' });
      fetchPeriods();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to remove daypart', variant: 'error' });
    }
  }, [fetchPeriods, toast]);

  return (
    <div className="animate-fade-in space-y-5">
      {/* Filter + create — relative z-20 keeps the dropdown above the cards below */}
      <div className={cn(GLASS, 'p-4 flex flex-wrap items-center gap-3 relative z-20')}>
        <div className="min-w-[220px]">
          <Select
            label="Location"
            options={filterOptions}
            value={filterLocationId}
            onChange={(v) => setFilterLocationId(typeof v === 'string' ? v : (v[0] ?? ALL_LOCATIONS))}
            placeholder="All Locations"
          />
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 flex-1 min-w-[180px]">
          Dayparts control which items are orderable at which times. Showing {filterLocationId ? (locationNameById.get(filterLocationId) ?? 'this outlet') : 'all outlets'}.
        </p>
        <Button variant="primary" size="sm" onClick={openCreate}>
          <PlusIcon /><span className="ml-1.5">New Daypart</span>
        </Button>
      </div>

      {/* Periods list */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} variant="rect" height={150} className="rounded-xl" />)}
        </div>
      ) : periods.length === 0 ? (
        <div className={cn(GLASS, 'p-10')}>
          <EmptyState
            variant="page"
            title="No dayparts yet"
            description="Create Breakfast, Lunch and Dinner windows and assign items to each."
            action={{ label: '+ New Daypart', onClick: openCreate }}
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {periods.map((p) => (
            <div key={p.id} className={cn(GLASS, 'p-5 flex flex-col gap-3')}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-100">{p.name}</h3>
                  <p className="text-sm font-mono tabular-nums text-brand-600 dark:text-brand-400">{hhmm(p.start_time)} – {hhmm(p.end_time)}</p>
                  <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5">{locationNameById.get(p.location_id) ?? '—'}</p>
                </div>
                <Badge variant="brand" size="sm">{(p.product_ids ?? []).length} items</Badge>
              </div>
              <div className="flex flex-wrap gap-1">
                {ALL_DAYS.map((d) => (
                  <span key={d} className={cn(
                    'inline-flex items-center justify-center w-7 h-6 rounded text-[10px] font-semibold',
                    (p.days_of_week ?? []).includes(d)
                      ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                      : 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-600',
                  )}>{DAY_LABELS[d]}</span>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button variant="secondary" size="sm" onClick={() => openEdit(p)}>Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => handleDeactivate(p)} className="text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20">Remove</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Daypart' : 'New Daypart'} variant="wide">
        <div className="space-y-4">
          {!editing && (
            <Select
              label="Location"
              options={locationOptions}
              value={modalLocationId}
              onChange={(v) => { setModalLocationId(typeof v === 'string' ? v : (v[0] ?? '')); setFormError(null); }}
              placeholder="Select a location"
            />
          )}

          {/* Presets */}
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button key={p.name} type="button" onClick={() => applyPreset(p)}
                className="rounded-full px-3 py-1.5 text-xs font-semibold bg-neutral-100 text-neutral-700 hover:bg-brand-100 hover:text-brand-700 dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-brand-900/40 transition-colors">
                {p.label} ({p.start}–{p.end})
              </button>
            ))}
            <span className="text-xs text-neutral-400 self-center">or set a custom window below</span>
          </div>

          <Input label="Daypart Name" value={name} onChange={(v) => { setName(v.slice(0, 100)); setFormError(null); }} placeholder="e.g. Breakfast, Lunch, Happy Hour" required />

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-200">Start time</span>
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className="h-11 w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 text-sm text-neutral-900 dark:text-neutral-100 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none" />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-200">End time</span>
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                className="h-11 w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 px-3 text-sm text-neutral-900 dark:text-neutral-100 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none" />
            </label>
          </div>

          <div>
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-2">Days</p>
            <div className="flex flex-wrap gap-2">
              {DAY_LABELS.map((label, idx) => (
                <button key={idx} type="button" onClick={() => toggleDay(idx)}
                  className={cn('rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition-colors',
                    days.includes(idx)
                      ? 'bg-brand-500 text-white ring-brand-600'
                      : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 ring-neutral-200 dark:ring-neutral-600 hover:bg-brand-100 dark:hover:bg-brand-900/30')}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Select
              label="Items in this daypart"
              variant="multi-select"
              options={products}
              value={productIds}
              onChange={(v) => { setProductIds(Array.isArray(v) ? v : (v ? [v] : [])); setFormError(null); }}
              placeholder="Select menu items…"
            />
            {productIds.length > 0 && (
              <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                {productIds.length} item{productIds.length > 1 ? 's' : ''}: {productIds.slice(0, 4).map((id) => productNameById.get(id) ?? id).join(', ')}{productIds.length > 4 ? '…' : ''}
              </p>
            )}
          </div>

          {formError && <p className="text-sm text-danger-600">{formError}</p>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={isSaving}>{editing ? 'Save Changes' : 'Create Daypart'}</Button>
          </div>
        </div>
      </Modal>

      <button type="button" onClick={() => navigate('/menus')} className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">← Back to Menu</button>
    </div>
  );
}

export function MenuPeriodsPage() {
  return (
    <PageContainer title="Menu Periods (Dayparts)" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Menus', href: '/menus' }, { label: 'Dayparts' }]}>
      <ErrorBoundary resetKey="menu-periods">
        <MenuPeriodsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
